import {
  Box,
  Button,
  Form,
  FormField,
  Heading,
  Select,
  Text,
  TextInput,
  Tip
} from 'grommet'
import React, { forwardRef, useEffect, useState } from 'react'
import ErrorProvider from './ErrorProvider'
import GlobalConfig from '../GlobalConfig'
import { Constants, I18n } from 'galarm-config'
import {
  stringNotEmpty,
  validateEmail,
  validateFirstName,
  validateLastName,
  validatePhoneNumber
} from '../utils/validations'
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore'
import { StringUtils, Utils } from 'galarm-shared'
import FormFieldWithAllBorders from './FormFieldWithAllBorders'
import { CircleInformation } from 'grommet-icons'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  equalTo,
  get,
  orderByChild,
  ref,
  query as dbQuery
} from 'firebase/database'
import { logEvent } from 'firebase/analytics'
import PhoneInput from 'react-phone-number-input'

import 'react-phone-number-input/style.css'
import { SecondaryText } from 'web-components'
import { FirebaseProxy } from 'galarm-ps-api'
import ReactSelect from 'react-select'
import { useSelector } from 'react-redux'

const roleOptions = [
  { label: I18n.t('admin'), value: Constants.ENTERPRISE_USER_ROLES.ADMIN },
  { label: I18n.t('viewer'), value: Constants.ENTERPRISE_USER_ROLES.VIEWER },
  {
    label: I18n.t('viewerNoLogin'),
    value: Constants.ENTERPRISE_USER_ROLES.VIEWER_NO_LOGIN
  },
  {
    label: I18n.t('incidentCoordinator'),
    value: Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
  }
]

const PhoneNumberTextInput = forwardRef((props, ref) => {
  return (
    <TextInput
      plain
      id="mobileNumber"
      name="mobileNumber"
      ref={ref}
      disabled={props.disabled}
    />
  )
})
PhoneNumberTextInput.displayName = 'PhoneNumberTextInput'

const EditMember = ({ isEdit, member, onAssignKey, onClose }) => {
  const enterpriseAccountId = window.localStorage.getItem('enterpriseAccountId')

  const alerts = useSelector(state => state.accountInfo.alerts)
  const availableLabels = useSelector(state => state.accountInfo.labels)

  const INITIAL_STATE = isEdit
    ? {
        mobileNumber: member.mobileNumber,
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        role: member.role,
        appUid: member.appUid,
        id: member.id,
        assignedListeners: member.assignedListeners || [],
        assignedLabels: member.assignedLabels || []
      }
    : {
        mobileNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        assignedListeners: [],
        assignedLabels: []
      }

  const [value, setValue] = useState(INITIAL_STATE)
  const [defaultCountryCode, setDefaultCountryCode] = useState('')

  const [errors, setErrors] = useState([])

  const addError = error => setErrors(errors.concat([error]))
  const onClearError = errorToClear => {
    const newErrors = errors.filter(error => error !== errorToClear)
    setErrors(newErrors)
  }

  useEffect(() => {
    if (isEdit) {
      return
    }

    const ipstackKey = FirebaseProxy.getRemoteConfigString('ipstackKey')

    if (!ipstackKey) {
      return
    }

    var url = `https://api.ipstack.com/check?access_key=${ipstackKey}`
    fetch(url)
      .then(response => {
        return response.json()
      })
      .then(responseJson => {
        if (
          responseJson.country_code &&
          responseJson.location &&
          responseJson.location.calling_code
        ) {
          setDefaultCountryCode(responseJson.country_code)
          setValue({
            ...value,
            mobileNumber: '+' + responseJson.location.calling_code
          })
        }
      })
      .catch(error => {
        console.error('error', error)
      })
  }, [])

  const onEditMember = async ({ value }) => {
    try {
      const {
        mobileNumber,
        firstName,
        lastName,
        email,
        role,
        appUid,
        id,
        assignedListeners,
        assignedLabels
      } = value
      const isCoordinator =
        role === Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
      // Coordinators have no phone field; ignore any hidden value left in state
      const isPhoneProvided = !isCoordinator && !!mobileNumber
      const sanitizedMobileNumber = isPhoneProvided
        ? Utils.sanitizePhoneNumber(mobileNumber)
        : ''

      if (
        isCoordinator &&
        (!assignedListeners || assignedListeners.length === 0) &&
        (!assignedLabels || assignedLabels.length === 0)
      ) {
        throw new Error(I18n.t('noListenersAssignedToCoordinator'))
      }

      let memberAppUid, memberId
      if (!isEdit) {
        if (!isCoordinator && !isPhoneProvided) {
          throw new Error(I18n.t('phoneNumberRequired'))
        }

        if (isPhoneProvided) {
          GlobalConfig.showProgress({
            state: Constants.ProgressStates.IN_PROGRESS,
            message: I18n.t('checkingIfMemberIsAppUser'),
            closeable: false
          })

          const usersSnapshot = await get(
            dbQuery(
              ref(GlobalConfig.realtimeDb, 'users'),
              orderByChild('mobileNumberWithCC'),
              equalTo(sanitizedMobileNumber)
            )
          )

          if (!usersSnapshot.val()) {
            throw new Error(I18n.t('noUserFoundWithMobileNumber'))
          }

          const members = Object.values(usersSnapshot.val()).filter(
            user => !user.accountInactive
          )

          if (members.length > 1) {
            throw new Error(I18n.t('multipleUsersWithSameMobileNumber'))
          }

          const member = members[0]

          if (!member) {
            throw new Error(I18n.t('noUserFoundWithMobileNumber'))
          }

          memberAppUid = member.id

          const memberQuery = query(
            collection(
              GlobalConfig.firestoreDb,
              'enterpriseAccounts',
              enterpriseAccountId,
              'members'
            ),
            where('appUid', '==', memberAppUid)
          )
          const memberQuerySnapshot = await getDocs(memberQuery)
          const memberExists = !memberQuerySnapshot.empty

          if (memberExists) {
            throw new Error(I18n.t('memberWithPhoneNumberAlreadyAdded'))
          }
        }

        const userWithEmailQuery = query(
          collection(
            GlobalConfig.firestoreDb,
            'enterpriseAccounts',
            enterpriseAccountId,
            'members'
          ),
          where('email', '==', email)
        )
        const userWithEmailQuerySnapshot = await getDocs(userWithEmailQuery)
        const userWithEmailExists = !userWithEmailQuerySnapshot.empty

        if (userWithEmailExists) {
          throw new Error(I18n.t('memberWithEmailAlreadyAdded'))
        }

        const enterpriseUserQuery = query(
          collection(GlobalConfig.firestoreDb, 'enterpriseUsers'),
          where('email', '==', email)
        )
        const enterpriseUserQuerySnapshot = await getDocs(enterpriseUserQuery)

        if (enterpriseUserQuerySnapshot.size === 1) {
          let enterpriseUser
          enterpriseUserQuerySnapshot.forEach(
            doc => (enterpriseUser = doc.data())
          )

          memberId = enterpriseUser.id
          await updateDoc(
            doc(GlobalConfig.firestoreDb, 'enterpriseUsers', memberId),
            {
              organizations: arrayUnion(enterpriseAccountId)
            }
          )
        } else if (enterpriseUserQuerySnapshot.size > 1) {
          throw new Error(I18n.t('multipleUsersWithSameEmail'))
        } else {
          GlobalConfig.showProgress({
            state: Constants.ProgressStates.IN_PROGRESS,
            message: I18n.t('creatingAnAccountForMember'),
            closeable: false
          })
          const displayName = StringUtils.createDisplayName(firstName, lastName)

          const functions = getFunctions()

          if (role === Constants.ENTERPRISE_USER_ROLES.VIEWER_NO_LOGIN) {
            const createEnterpriseUserWithoutEmail = httpsCallable(
              functions,
              'createEnterpriseUserWithoutEmail'
            )
            const result = await createEnterpriseUserWithoutEmail({
              firstName,
              lastName,
              enterpriseAccountId,
              displayName: displayName
            })
            memberId = result.data.uid
          } else {
            const createUserWithEmail = httpsCallable(
              functions,
              'createUserWithEmail'
            )
            const result = await createUserWithEmail({
              firstName,
              lastName,
              email: email,
              enterpriseAccountId,
              displayName: displayName
            })
            memberId = result.data.uid
          }
        }
      } else {
        memberId = id
        memberAppUid = appUid
      }

      GlobalConfig.showProgress({
        state: Constants.ProgressStates.IN_PROGRESS,
        message: I18n.t('settingMemberDetails'),
        closeable: false
      })

      const memberToAdd = {
        id: memberId,
        firstName,
        lastName,
        email,
        mobileNumber: sanitizedMobileNumber,
        role: role,
        ...(memberAppUid ? { appUid: memberAppUid } : {}),
        assignedListeners: isCoordinator ? assignedListeners : [],
        assignedLabels: isCoordinator ? assignedLabels : []
      }

      await setDoc(
        doc(
          GlobalConfig.firestoreDb,
          'enterpriseAccounts',
          enterpriseAccountId,
          'members',
          memberId
        ),
        memberToAdd,
        { merge: true }
      )

      !isEdit && onAssignKey(memberToAdd)

      GlobalConfig.hideProgress()

      logEvent(
        GlobalConfig.analytics,
        isEdit
          ? Constants.UserAnalyticsEvents.EDIT_MEMBER
          : Constants.UserAnalyticsEvents.ADD_MEMBER,
        {}
      )

      onClose()
    } catch (error) {
      console.error(error)
      addError(error.message)
      GlobalConfig.hideProgress()
    }
  }

  return (
    <ErrorProvider
      errors={errors}
      getErrorMessage={error => error}
      onClearError={onClearError}>
      <Box align="center" flex={{ shrink: 0 }}>
        <Heading level={4}>
          {isEdit ? I18n.t('editMember') : I18n.t('addMember')}
        </Heading>
        <Box
          background="white"
          pad="small"
          round="small"
          margin="medium"
          width="large">
          <Form
            value={value}
            onChange={nextValue => setValue(nextValue)}
            validate="submit"
            onSubmit={userData => {
              onEditMember(userData)
            }}>
            <FormFieldWithAllBorders
              htmlFor="role"
              name="role"
              label={
                <Box direction="row" align="center" gap="xsmall">
                  <SecondaryText weight={500}>{I18n.t('role')}</SecondaryText>
                  <Tip
                    content={
                      <Box pad="small" gap="small" width="large">
                        {[
                          {
                            name: I18n.t('admin'),
                            description: I18n.t(
                              'enterpriseAdminRoleDescription'
                            )
                          },
                          {
                            name: I18n.t('viewer'),
                            description: I18n.t(
                              'enterpriseViewerRoleDescription'
                            )
                          },
                          {
                            name: I18n.t('viewerNoLogin'),
                            description: I18n.t(
                              'enterpriseViewerNoLoginRoleDescription'
                            )
                          },
                          {
                            name: I18n.t('incidentCoordinator'),
                            description: I18n.t(
                              'enterpriseIncidentCoordinatorRoleDescription'
                            )
                          }
                        ].map(({ name, description }) => (
                          <Box key={name}>
                            <Text weight="bold">{name}</Text>
                            <Text>{description}</Text>
                          </Box>
                        ))}
                      </Box>
                    }>
                    <CircleInformation color="textColor" size="18px" />
                  </Tip>
                </Box>
              }
              validate={stringNotEmpty.bind(null, I18n.t('role'))}>
              <Select
                id="role"
                name="role"
                disabled={isEdit}
                options={roleOptions}
                placeholder={I18n.t('chooseRoleForMember')}
                labelKey="label"
                valueKey={{ key: 'value', reduce: true }}
                onChange={({ option }) => {
                  setValue({
                    ...value,
                    role: option.value,
                    assignedListeners:
                      option.value !==
                      Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
                        ? []
                        : value.assignedListeners,
                    assignedLabels:
                      option.value !==
                      Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR
                        ? []
                        : value.assignedLabels
                  })
                }}
              />
            </FormFieldWithAllBorders>
            {(isEdit || value.role) && (
              <>
                {value.role !==
                  Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR && (
                  <FormField
                    disabled={isEdit}
                    htmlFor="mobileNumber"
                    name="mobileNumber"
                    label={
                      <SecondaryText weight={500}>
                        {I18n.t('mobileNumber')}
                      </SecondaryText>
                    }
                    validate={validatePhoneNumber}>
                    <PhoneInput
                      disabled={isEdit}
                      style={{ marginLeft: 10 }}
                      international
                      onChange={mobileNumber =>
                        setValue({ ...value, mobileNumber })
                      }
                      countrySelectProps={{ unicodeFlags: true }}
                      inputComponent={PhoneNumberTextInput}
                      defaultCountry={defaultCountryCode}
                    />
                  </FormField>
                )}
                <Box direction="row" gap="medium">
                  <FormField
                    flex
                    htmlFor="firstName"
                    name="firstName"
                    label={
                      <SecondaryText weight={500}>
                        {I18n.t('firstName')}
                      </SecondaryText>
                    }
                    validate={validateFirstName}>
                    <TextInput id="firstName" name="firstName" />
                  </FormField>
                  <FormField
                    flex
                    htmlFor="lastName"
                    name="lastName"
                    label={
                      <SecondaryText weight={500}>
                        {I18n.t('lastName')}
                      </SecondaryText>
                    }
                    validate={validateLastName}>
                    <TextInput id="lastName" name="lastName" />
                  </FormField>
                </Box>
                {value.role !==
                  Constants.ENTERPRISE_USER_ROLES.VIEWER_NO_LOGIN && (
                  <FormField
                    disabled={isEdit}
                    htmlFor="email"
                    name="email"
                    label={
                      <SecondaryText weight={500}>
                        {I18n.t('email')}
                      </SecondaryText>
                    }
                    validate={validateEmail}>
                    <TextInput id="email" name="email" disabled={isEdit} />
                  </FormField>
                )}
                {value.role ===
                  Constants.ENTERPRISE_USER_ROLES.INCIDENT_COORDINATOR && (
                  <>
                    <FormFieldWithAllBorders
                      htmlFor="assignedListeners"
                      name="assignedListeners"
                      label={
                        <SecondaryText weight={500}>
                          {I18n.t('assignedListeners')}
                        </SecondaryText>
                      }>
                      <ReactSelect
                        id="assignedListeners"
                        name="assignedListeners"
                        styles={{
                          control: provided => ({
                            ...provided,
                            border: 'none'
                          }),
                          menuPortal: provided => ({
                            ...provided,
                            zIndex: 9999
                          })
                        }}
                        isMulti
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={alerts}
                        getOptionLabel={alert => alert.name}
                        getOptionValue={alert => alert.id}
                        onChange={selectedAlerts => {
                          setValue({
                            ...value,
                            assignedListeners: (selectedAlerts || []).map(
                              a => a.id
                            )
                          })
                        }}
                        value={alerts.filter(a =>
                          (value.assignedListeners || []).includes(a.id)
                        )}
                      />
                    </FormFieldWithAllBorders>
                    <FormFieldWithAllBorders
                      htmlFor="assignedLabels"
                      name="assignedLabels"
                      label={
                        <SecondaryText weight={500}>
                          {I18n.t('assignedLabels')}
                        </SecondaryText>
                      }>
                      <ReactSelect
                        id="assignedLabels"
                        name="assignedLabels"
                        styles={{
                          control: provided => ({
                            ...provided,
                            border: 'none'
                          }),
                          menuPortal: provided => ({
                            ...provided,
                            zIndex: 9999
                          })
                        }}
                        isMulti
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        options={availableLabels}
                        getOptionLabel={label => label.name}
                        getOptionValue={label => label.id}
                        onChange={selectedLabels => {
                          setValue({
                            ...value,
                            assignedLabels: (selectedLabels || []).map(
                              l => l.id
                            )
                          })
                        }}
                        value={availableLabels.filter(l =>
                          (value.assignedLabels || []).includes(l.id)
                        )}
                      />
                    </FormFieldWithAllBorders>
                  </>
                )}
                <Box
                  direction="row"
                  gap="medium"
                  justify="center"
                  margin="medium">
                  <Button
                    type="submit"
                    primary
                    style={{ color: 'white' }}
                    label={isEdit ? I18n.t('save') : I18n.t('add')}
                  />
                </Box>
              </>
            )}
          </Form>
        </Box>
      </Box>
    </ErrorProvider>
  )
}

export default EditMember
